import { factor } from '@fameacc/money';
import { z } from 'zod';
/**
 * Die Vertraege der Steuersatzquelle — Migration `0176`, Chip `ST-quelle`.
 *
 * Spec `docs/superpowers/specs/2026-09-06-steuerlogik-design.md`, Abschnitt 2
 * (`D-1` bis `D-5`, `D-7`, `D-8`, `D-12`, `D-32`).
 *
 * **Die zwei Einheiten stehen nebeneinander und sind getrennt benannt**
 * (`D-3`). Das ist der ganze Zweck dieser Datei: Die Ausgangsseite dieses
 * Systems rechnet in **Faktoren** (`document_line.tax_rate`, Bedingung
 * `>= 0 AND < 1`), die Eingangsseite in **Prozent**
 * (`incoming_invoice_tax_line.tax_rate`) — und die zwei bestehenden Vertraege
 * sagen in ihren eigenen Fehlermeldungen woertlich das Gegenteil voneinander.
 * Wer hier `satzFaktor` und `satzProzent` liest, kann sich nicht mehr irren,
 * weil beide dastehen und keiner der beiden aus dem anderen abgeleitet wird.
 *
 * **Beide sind `Factor` und niemals `number`** (Hausregel 1). Ein Satz, der
 * als Zeichenkette aus SQL kommt, ist ein Geschenk: Er wird nicht durch
 * `parseFloat`, `Number(...)` oder `.toFixed(2)` geschickt, sondern bleibt,
 * was er ist. `Factor` und nicht `Money`, weil ein Satz dimensionslos ist und
 * nicht auf zwei Nachkommastellen aufgefuellt wird.
 *
 * **Kein Schreibvertrag.** Diese Tabellen werden ausschliesslich von
 * Migrationen beschrieben (`D-1`), und `fameacc_app` hat auf beiden nur
 * `SELECT` (`E-949`). Ein Anlege- oder Aenderungsschema waere ein Vertrag
 * ueber einen Weg, den es nicht gibt.
 */
/**
 * Die drei Satzarten.
 *
 * `null_prozent` ist eine eigene Art und kein ermaessigter Satz von null: Der
 * Nullsatz traegt jedes steuerfreie und jedes nicht steuerbare Bild (Reverse
 * Charge, Drittland) und hat mit § 12 Abs. 2 UStG nichts zu tun.
 *
 * Deckungsgleich mit der Aufzaehlung `steuersatz_art` aus Migration `0176`.
 */
export const steuersatzArtSchema = z.enum(['regel', 'ermaessigt', 'null_prozent']);
/**
 * Was ein Steuerschluessel im Buchungsstoff bewirkt.
 *
 * Deckungsgleich mit der Aufzaehlung `steuerschluessel_wirkung` aus Migration
 * `0176`. `keine` ist der Hauswert `'0'` (`D-7`).
 */
export const steuerschluesselWirkungSchema = z.enum(['umsatzsteuer', 'vorsteuer', 'keine']);
/**
 * Ein Datum in ISO-Form, so wie es aus `date`-Spalten kommt.
 *
 * Eine eigene Zeile statt `z.string()`, damit ein vertauschtes Feld —
 * `fundstelle` statt `validFrom` — hier auffaellt und nicht erst beim
 * Vergleichen zweier Daten.
 */
const isoDatumSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet wird ein Datum JJJJ-MM-TT.');
/**
 * Ein Satz als markierte Zeichenkette.
 *
 * `factor(...)` wirft bei allem, was keine kanonische Dezimalzeichenkette ist
 * — der Vertrag gibt damit einen `Factor` zurueck und nie eine rohe Eingabe.
 */
const satzSchema = z
    .string()
    .transform((wert, ctx) => {
    try {
        return factor(wert);
    }
    catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Kein gültiger Satz: ${wert}` });
        return z.NEVER;
    }
});
/**
 * Eine Zeile der Tabelle `steuersatz`.
 *
 * `validTo` ist **ausschliessend**: Am Wechseltag zweier Fassungen gilt die
 * neue. Wer beim Auswerten `validTo >= stichtag` schreibt, bekommt am
 * 01.07.2020 zwei Saetze, 19 % und 16 % (`E-947`).
 */
export const steuersatzZeileSchema = z
    .object({
    land: z.string().length(2),
    art: steuersatzArtSchema,
    validFrom: isoDatumSchema,
    validTo: isoDatumSchema.nullable(),
    /** `'0.190000'`. */
    satzFaktor: satzSchema,
    /** `'19.00'` — dieselbe Zahl, andere Einheit (`D-3`). */
    satzProzent: satzSchema,
    fundstelle: z.string().min(1),
})
    .strict();
/**
 * Eine Zeile der Tabelle `steuerschluessel`.
 *
 * **Kein Satz als Zahl.** `satzLand`/`satzArt` zeigen in `steuersatz`; der
 * Satz selbst haengt am Leistungsstichtag und steht deshalb nirgends zweimal.
 * Beide Felder sind zusammen NULL oder zusammen gesetzt — ein kuenftiger
 * Schluessel, der eine Anweisung statt eines Satzes traegt (Berichtigung,
 * Generalumkehr), hat keinen.
 */
export const steuerschluesselZeileSchema = z
    .object({
    schluessel: z.string().min(1),
    validFrom: isoDatumSchema,
    validTo: isoDatumSchema.nullable(),
    satzLand: z.string().length(2).nullable(),
    satzArt: steuersatzArtSchema.nullable(),
    wirkung: steuerschluesselWirkungSchema,
    /** Zeile der Voranmeldung. `null` heisst „noch nicht eingetragen" (`D-32`). */
    ustvaKennzahl: z.string().nullable(),
    zmPflichtig: z.boolean(),
    ossRelevant: z.boolean(),
    datevJahrgang: z.number().int(),
    /** Die Spalte „BU bisher". Ein eigener Schluessel traegt hier nie etwas (`D-7`). */
    datevBuDreistellig: z.string().nullable(),
    eigen: z.boolean(),
    hinweis: z.string().min(1),
})
    .strict()
    .refine((zeile) => (zeile.satzLand === null) === (zeile.satzArt === null), {
    message: 'satzLand und satzArt stehen zusammen oder gar nicht.',
})
    .refine((zeile) => !(zeile.eigen && zeile.datevBuDreistellig !== null), {
    message: 'Ein eigener Steuerschlüssel trägt kein dreistelliges BU-Feld.',
});
/**
 * Der eingecheckte Abdruck `packages/schema/steuerquelle.json` (`D-5`).
 *
 * Die Felder mit fuehrendem Unterstrich sind Prosa fuer den Leser des Diffs
 * und nicht Teil der Aussage; sie stehen im Vertrag, damit `.strict()` an den
 * echten Feldern scharf bleiben kann.
 */
export const steuerquelleAbdruckSchema = z
    .object({
    _hinweis: z.string(),
    _einheiten: z.string(),
    _datenstand: z.string(),
    steuersatz: z.array(steuersatzZeileSchema),
    steuerschluessel: z.array(steuerschluesselZeileSchema),
})
    .strict();
//# sourceMappingURL=quelle.js.map